import JSZip from 'jszip';
import { useCallback, useEffect, useRef } from 'react';
import { useAlert, useSync, useUserContext } from '@/hooks';
import { yieldToMain } from '@/shared/yieldToMain';
import { atualizarProgressoTopo, finalizarProgressoTopo, iniciarProgressoTopo } from '@/shared/topProgress';
import { lerDiretorioZip, mimeDoArquivo, percorrerArquivosZip, type EntradaZip } from '@/shared/zipReader';
import type { ImagemParaGravar } from '../dbFunctions/useDBFunctions';
import { useDbFunctions } from '../dbFunctions/useDBFunctions';

/**
 * Descompacta, em segundo plano, os zips que o sync guardou na store `images_zip`.
 *
 * O sync só baixa e grava o arquivo — abrir o zip, converter cada entrada em `Blob` e gravar
 * imagem por imagem custa caro e não precisa acontecer com o usuário parado na tela de download.
 * Este talon faz esse trabalho depois, enquanto o app já está em uso.
 *
 * O que faz esse trabalho caber num aparelho fraco é **como o zip é lido**: em blocos grandes e
 * sequenciais (`percorrerArquivosZip`), com as imagens saindo de dentro do bloco já carregado.
 *
 * Duas alternativas mais óbvias são piores, e as duas foram medidas:
 *
 *  - **`JSZip.loadAsync`** precisa do arquivo inteiro em memória antes da primeira entrada. Num
 *    aparelho de 2GB, dezenas de MB viram o dobro disso em heap e o coletor de lixo passa a rodar
 *    no meio do trabalho. Ficou como plano B, para zip que o leitor novo não entende;
 *  - **uma fatia por imagem** (`blob.slice` por entrada) gasta pouquíssima memória, mas troca uma
 *    leitura sequencial por milhares de leituras espalhadas, cada uma com ida e volta ao
 *    subsistema de Blob do navegador. Em flash lento isso é **mais lento que o JSZip**, não menos.
 *
 * O ritmo é dado por lote, não por imagem: extrair uma imagem da janela é uma cópia de alguns KB,
 * e o que realmente espera é o IndexedDB — que já roda fora do main thread. Pausar a cada imagem
 * só somaria espera.
 *
 * Se o app for fechado no meio, o zip continua lá e o processo recomeça na próxima abertura —
 * regravar uma imagem que já entrou é um `put` sobre a mesma chave, não duplica nada.
 */

/** Espera o boot da tela terminar: o primeiro render não disputa CPU com a descompactação. */
const DELAY_INICIAL_MS = 5000;

/** Imagens por transação. O mesmo lote que o sync usava quando ele ainda descompactava. */
const LOTE_GRAVACAO = 200;

/** Lotes entre um respiro e outro. O `yieldToMain` de cada lote já devolve o main thread. */
const LOTES_ATE_RESPIRO = 5;

/** Teto do respiro. Curto de propósito: num aparelho ocupado, ele é sempre o que vale. */
const IDLE_TIMEOUT_MS = 300;

/** Bloco de leitura do zip. Menor onde a memória é curta; nunca por imagem. */
const getTamanhoJanela = (): number => {
  const memoria = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  return memoria !== undefined && memoria <= 2 ? 2 * 1024 * 1024 : 4 * 1024 * 1024;
};

/** De quanto em quanto tempo se pergunta de novo — um sync em background pode trazer zip novo. */
const INTERVALO_VERIFICACAO_MS = 60 * 1000;

const EXTENSAO_IMAGEM = /\.(webp|png|jpe?g)$/i;

/** Uma descompactação por app, não por montagem do componente. */
let descompactacaoEmAndamento = false;

const getProductCodeFromFilename = (filename: string): number | null => {
  const onlyNumbers = filename.split('/').pop()?.replace(/\D+/g, '');
  const code = Number(onlyNumbers);
  return Number.isFinite(code) && code > 0 ? code : null;
};

/** Devolve o main thread e só volta quando o navegador respirar (ou o teto estourar). */
const esperarOcioso = (): Promise<void> => {
  const requestIdle = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
    .requestIdleCallback;

  if (typeof requestIdle !== 'function') return yieldToMain();

  return new Promise((resolve) => {
    requestIdle(() => resolve(), { timeout: IDLE_TIMEOUT_MS });
  });
};

const ehImagem = (nome: string) => EXTENSAO_IMAGEM.test(nome);

export const useUnpackImagesZip = () => {
  const { showAlert } = useAlert();
  const { isSignedIn } = useUserContext();
  const { isLoading, preparing, isOpenedModal } = useSync();
  const db = useDbFunctions();

  const dbRef = useRef(db);
  dbRef.current = db;

  /**
   * O `Container` envolve o login também, e é lá que o sync roda. Descompactar junto disputaria
   * CPU e disco com o download — e o zip que se pretende abrir pode ser justamente o que ainda
   * está sendo gravado. Em ref para não recriar o intervalo a cada render do sync.
   */
  const syncOcupadoRef = useRef(false);
  syncOcupadoRef.current = isLoading || preparing || isOpenedModal;

  /**
   * Grava o zip lendo em blocos sequenciais.
   *
   * As imagens entram por cima das que já existem, sem limpar a store: o processo roda com o
   * catálogo na tela, e zerar as imagens deixaria a vitrine sem foto até o último lote entrar.
   * Imagem de produto que saiu do zip fica para trás ocupando disco — invisível, já que o produto
   * também saiu do catálogo — e o `deleteImagesDB` do reset de versão resolve.
   *
   * Devolve `null` quando alguma entrada não pôde ser extraída assim; aí o zip inteiro é refeito
   * pelo `JSZip`. O que já entrou não atrapalha: regravar é `put` sobre a mesma chave.
   */
  const gravarPorJanelas = useCallback(
    async (zipBlob: Blob, entradas: EntradaZip[], aoAndar: (feitas: number, total: number) => void): Promise<number | null> => {
      const sessao = await dbRef.current.abrirSessaoImagens();
      const total = entradas.filter((entrada) => !entrada.ehDiretorio && ehImagem(entrada.nome)).length;

      let lote: Record<number, ImagemParaGravar> = {};
      let noLote = 0;
      let lotes = 0;
      let vistas = 0;
      let gravadas = 0;
      let msGravacao = 0;

      const descarregar = async () => {
        if (!noLote) return;

        const tGravacao = Date.now();
        gravadas += await sessao.gravar(lote);
        msGravacao += Date.now() - tGravacao;

        lote = {};
        noLote = 0;
        lotes += 1;

        // Um por lote, e não por imagem: é aqui que o main thread volta a ser do app.
        await yieldToMain();
        if (lotes % LOTES_ATE_RESPIRO === 0) await esperarOcioso();
      };

      try {
        const tTotal = Date.now();

        const completo = await percorrerArquivosZip(
          zipBlob,
          entradas,
          async (entrada, conteudo) => {
            vistas += 1;
            aoAndar(vistas, total);

            const code = getProductCodeFromFilename(entrada.nome);
            if (!code) return;

            lote[code] = { bytes: conteudo, tipo: mimeDoArquivo(entrada.nome) };
            noLote += 1;
            if (noLote >= LOTE_GRAVACAO) await descarregar();
          },
          { filtro: (entrada) => ehImagem(entrada.nome), tamanhoJanela: getTamanhoJanela() }
        );

        if (!completo) return null;

        await descarregar();

        const msTotal = Date.now() - tTotal;
        console.log(
          `[imagens] via janela: ${gravadas} imagens em ${msTotal}ms (leitura ${msTotal - msGravacao}ms, gravação ${msGravacao}ms)`
        );

        return gravadas;
      } finally {
        sessao.fechar();
      }
    },
    []
  );

  /** Plano B: o leitor completo. Carrega o zip inteiro na memória — só quando não há saída. */
  const gravarPeloJSZip = useCallback(
    async (zipBlob: Blob, aoAndar: (feitas: number, total: number) => void): Promise<number> => {
      const sessao = await dbRef.current.abrirSessaoImagens();

      let lote: Record<number, ImagemParaGravar> = {};
      let noLote = 0;
      let lotes = 0;
      let vistas = 0;
      let gravadas = 0;
      let msGravacao = 0;

      const descarregar = async () => {
        if (!noLote) return;

        const tGravacao = Date.now();
        gravadas += await sessao.gravar(lote);
        msGravacao += Date.now() - tGravacao;

        lote = {};
        noLote = 0;
        lotes += 1;
        await yieldToMain();
        if (lotes % LOTES_ATE_RESPIRO === 0) await esperarOcioso();
      };

      try {
        const tTotal = Date.now();
        const zip = await JSZip.loadAsync(zipBlob);
        const arquivos = Object.values(zip.files).filter((file) => !file.dir && ehImagem(file.name));

        for (const arquivo of arquivos) {
          vistas += 1;
          aoAndar(vistas, arquivos.length);

          const code = getProductCodeFromFilename(arquivo.name);
          if (!code) continue;

          lote[code] = { bytes: await arquivo.async('uint8array'), tipo: mimeDoArquivo(arquivo.name) };
          noLote += 1;
          if (noLote >= LOTE_GRAVACAO) await descarregar();
        }

        await descarregar();

        const msTotal = Date.now() - tTotal;
        console.log(
          `[imagens] via JSZip: ${gravadas} imagens em ${msTotal}ms (leitura ${msTotal - msGravacao}ms, gravação ${msGravacao}ms)`
        );

        return gravadas;
      } finally {
        sessao.fechar();
      }
    },
    []
  );

  const descompactarZip = useCallback(
    async (zipBlob: Blob, aoAndar: (feitas: number, total: number) => void): Promise<number> => {
      const entradas = await lerDiretorioZip(zipBlob);

      if (entradas) {
        const gravadas = await gravarPorJanelas(zipBlob, entradas, aoAndar);
        if (gravadas != null) return gravadas;
        console.info('[imagens] entrada não extraível em bloco, refazendo o zip com o leitor completo');
      } else {
        console.info('[imagens] diretório do zip ilegível (ZIP64?), indo direto para o leitor completo');
      }

      return gravarPeloJSZip(zipBlob, aoAndar);
    },
    [gravarPorJanelas, gravarPeloJSZip]
  );

  const processarZips = useCallback(async () => {
    if (descompactacaoEmAndamento || syncOcupadoRef.current || !isSignedIn) return;

    const urls = await dbRef.current.getImagesZipKeysFromDB();
    if (!urls.length) return;

    descompactacaoEmAndamento = true;
    iniciarProgressoTopo();

    showAlert({
      title: 'Estamos preparando as imagens dos produtos.',
      message: 'A descompactação está acontecendo em segundo plano — pode continuar usando o app normalmente.',
      theme: 'information',
      bgType: 'primary',
      bordered: true,
      borderLeft: true,
      isList: false,
      close: 'icon',
      time: 8000,
    });

    const tInicio = Date.now();
    let gravadas = 0;
    let zipsConcluidos = 0;

    // A barra cobre o run inteiro: cada zip vale uma fatia igual, e o andamento dentro dele
    // preenche essa fatia. Com um zip só — o caso normal — é o progresso dele mesmo.
    const aoAndar = (indiceDoZip: number) => (feitas: number, total: number) => {
      const fatia = total > 0 ? feitas / total : 0;
      atualizarProgressoTopo(indiceDoZip + fatia, urls.length);
    };

    try {
      for (const [indice, url] of urls.entries()) {
        try {
          const row = await dbRef.current.getImagesZipFromDB(url);

          // Registro sem blob não tem o que descompactar e só ocuparia a store para sempre.
          if (!(row?.zip instanceof Blob) || !row.zip.size) {
            await dbRef.current.deleteImagesZipFromDB(url);
            continue;
          }

          gravadas += await descompactarZip(row.zip, aoAndar(indice));

          // Só depois das imagens gravadas: se o app cair no meio, o zip continua lá para a
          // próxima tentativa.
          await dbRef.current.deleteImagesZipFromDB(url);
          zipsConcluidos += 1;
        } catch (error) {
          console.error(`[imagens] falha ao descompactar o zip ${url}:`, error);
        }
      }

      console.log(`[imagens] ${gravadas} imagens de ${zipsConcluidos} zip(s) em ${Date.now() - tInicio}ms`);

      // Nenhum zip concluído significa que todos falharam: aí o verde mentiria. O erro já foi
      // registrado no console e o zip continua no banco para a próxima tentativa.
      if (zipsConcluidos > 0) {
        showAlert({
          title: 'Imagens dos produtos prontas.',
          message: 'Imagens dos produtos foram salvas no aparelho.',
          theme: 'success',
          bgType: 'primary',
          bordered: true,
          borderLeft: true,
          isList: false,
          close: 'icon',
          time: 6000,
        });
      }
    } finally {
      finalizarProgressoTopo();
      descompactacaoEmAndamento = false;
    }
  }, [descompactarZip, showAlert]);

  useEffect(() => {
    let cancelado = false;

    const verificar = () => {
      if (cancelado) return;
      void processarZips().catch((error) => console.error('[imagens] falha ao descompactar em segundo plano:', error));
    };

    const inicio = setTimeout(verificar, DELAY_INICIAL_MS);
    // A verificação repetida é só um `getAllKeys` numa store quase sempre vazia: um sync em
    // background durante a sessão traz zip novo, e sem isso ele esperaria a próxima abertura.
    // É também o que faz a próxima tentativa acontecer quando o tick cai durante um sync.
    const intervalo = setInterval(verificar, INTERVALO_VERIFICACAO_MS);

    return () => {
      cancelado = true;
      clearTimeout(inicio);
      clearInterval(intervalo);
    };
  }, [processarZips, isSignedIn]);
};
